/**
 * Таблица «Хозяйства» экрана «Холдинг» (AK-903, макет v2 экран 4).
 *
 * Хозяйство · кластер · пашня (учётная) · полей · двойник «N из 6» с
 * мини-полосой · «что не заполнено» · →. По умолчанию незаполненные первыми
 * — руководитель УК начинает день с проблемных хозяйств (В-2), остальные
 * порядки и поиск — на сервере (`?sort=&q=`), страница — `limit`.
 *
 * Клик по строке = вход в хозяйство: ОДИН `POST /scope/set` (общий
 * `useScopeSetter`, как в переключателе) → `/farm/<id>` «Данные хозяйства».
 * Переключатель наверху меню покажет это хозяйство в тот же кадр
 * (оптимистичный `current`). Отказ сервера — ошибка под таблицей, перехода нет:
 * молчащий set — урок AK-790.
 *
 * Пустое состояние различает «хозяйств нет» (ссылка в Структуру) и «по поиску
 * ничего» — это разные ответы на разные вопросы.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Search } from 'lucide-react';
import { ErrorState } from '@/components/ErrorState';
import { useScopeSetter } from '@/components/scope/useScopeSetter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { pluralRu } from '@/lib/dateFmt';
import { cn } from '@/lib/utils';
import {
  DEFAULT_FARMS_TWIN_PARAMS,
  FARMS_TWIN_PAGE,
  formatHa,
  missingTitles,
  useFarmsTwin,
  type FarmTwinRow,
  type FarmsTwinSort,
} from './farmsTwin';

const SORT_LABELS: Record<FarmsTwinSort, string> = {
  incomplete_first: 'Сначала незаполненные',
  cluster: 'По кластерам',
  name: 'По алфавиту',
};

/** Мини-полоса двойника: сегмент на шаг, закрытые — зелёные. */
function TwinMiniBar({ row }: { row: FarmTwinRow }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="flex w-20 gap-1" aria-hidden>
        {row.steps.map((s) => (
          <span
            key={s.key}
            className={cn('h-1.5 flex-1 rounded-sm', s.done ? 'bg-primary' : 'bg-muted')}
          />
        ))}
      </span>
      <span className="text-sm text-foreground">
        {row.done_count} из {row.total_steps}
      </span>
    </span>
  );
}

function RowsSkeleton() {
  return (
    <div className="space-y-2 p-4" data-testid="farms-twin-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

export interface FarmsTwinTableProps {
  className?: string;
}

export function FarmsTwinTable({ className }: FarmsTwinTableProps) {
  const navigate = useNavigate();
  const { setScope, saving, error: scopeError, clearError } = useScopeSetter('FarmsTwinTable');

  const [sort, setSort] = useState<FarmsTwinSort>(DEFAULT_FARMS_TWIN_PARAMS.sort);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(FARMS_TWIN_PAGE);

  // Поиск уходит на сервер после паузы в наборе, а не на каждую букву.
  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const query = useFarmsTwin({ sort, q, limit });
  const data = query.data;

  // Хозяйство последнего клика — чтобы «Повторить» под плашкой ошибки
  // ПОВТОРЯЛ вход в него, а не просто гасил текст (ревью head-web #1119).
  const lastFarm = useRef<FarmTwinRow | null>(null);

  const openFarm = useCallback(
    async (farm: FarmTwinRow) => {
      if (saving) return;
      lastFarm.current = farm;
      // `setScope` сам сбрасывает прежнюю ошибку перед запросом.
      const ok = await setScope({ scope_type: 'farm', scope_id: farm.id });
      // Сервер отказал — перехода нет: человек видит плашку, а не экран
      // хозяйства при прежней области в переключателе.
      if (!ok) return;
      void navigate({ to: '/farm/$farmId', params: { farmId: farm.id } });
    },
    [navigate, saving, setScope],
  );

  const retryOpen = useCallback(() => {
    if (lastFarm.current) void openFarm(lastFarm.current);
    else clearError();
  }, [openFarm, clearError]);

  const rows = data?.farms ?? [];
  const total = data?.total ?? 0;
  const hasMore = !!data && rows.length < data.total;
  const filtered = q.length > 0;

  return (
    <Card className={cn('mt-6', className)} data-testid="farms-twin-table">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0 p-4 pb-3">
        <CardTitle>
          Хозяйства{data ? ` · ${data.totals.farms}` : ''}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={sort} onValueChange={(v) => setSort(v as FarmsTwinSort)}>
            <TabsList aria-label="Порядок хозяйств">
              {(Object.keys(SORT_LABELS) as FarmsTwinSort[]).map((key) => (
                <TabsTrigger key={key} value={key}>
                  {SORT_LABELS[key]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Найти хозяйство…"
              aria-label="Найти хозяйство"
              className="h-9 w-48 pl-8"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {query.isError && (
          <div className="p-4">
            <ErrorState
              variant="inline"
              message="Не удалось загрузить хозяйства."
              onRetry={() => void query.refetch()}
            />
          </div>
        )}
        {!data && !query.isError && <RowsSkeleton />}
        {data && rows.length === 0 && !filtered && (
          <div className="p-4 text-sm text-muted-foreground" data-testid="farms-twin-empty">
            Хозяйств нет — создайте в{' '}
            <Link to="/structure" className="text-primary underline-offset-4 hover:underline">
              Компания → Структура
            </Link>
            .
          </div>
        )}
        {data && rows.length === 0 && filtered && (
          <div className="p-4 text-sm text-muted-foreground">
            По запросу «{q}» хозяйств не найдено.
          </div>
        )}
        {data && rows.length > 0 && (
          <div className="overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Хозяйство</TableHead>
                  <TableHead>Кластер</TableHead>
                  <TableHead className="text-right">Пашня</TableHead>
                  <TableHead className="text-right">Полей</TableHead>
                  <TableHead>Двойник</TableHead>
                  <TableHead>Что не заполнено</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((farm) => (
                  <TableRow
                    key={farm.id}
                    data-testid={`farm-row-${farm.id}`}
                    className="cursor-pointer"
                    onClick={() => void openFarm(farm)}
                  >
                    <TableCell className="font-medium text-foreground">{farm.name}</TableCell>
                    <TableCell className="text-muted-foreground">{farm.cluster_name ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatHa(farm.arable_ha)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {farm.fields_active.toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell>
                      <TwinMiniBar row={farm} />
                    </TableCell>
                    <TableCell className="max-w-[320px] text-sm text-muted-foreground">
                      {missingTitles(farm, data.steps)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Открыть данные хозяйства «${farm.name}»`}
                        disabled={saving}
                        onClick={(e) => {
                          e.stopPropagation();
                          void openFarm(farm);
                        }}
                      >
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {scopeError && (
          <div className="p-4">
            <ErrorState
              variant="inline"
              message={`Не удалось открыть хозяйство: ${scopeError}`}
              onRetry={retryOpen}
            />
          </div>
        )}
        {data && rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-4 text-sm text-muted-foreground">
            <span>
              Показано {rows.length} из {total} {pluralRu(total, ['хозяйства', 'хозяйств', 'хозяйств'])}
            </span>
            {hasMore && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLimit((l) => l + FARMS_TWIN_PAGE)}
                disabled={query.isFetching}
              >
                Показать ещё
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FarmsTwinTable;
